class FreeProductsProgress extends HTMLElement {
  constructor() {
    super();
    this.cartTotal = parseInt(this.getAttribute('data-cart-total')) || 0;
    this.threshold1 = parseInt(this.getAttribute('data-threshold-1')) || 0;
    this.threshold2 = parseInt(this.getAttribute('data-threshold-2')) || 0;
    this.threshold3 = parseInt(this.getAttribute('data-threshold-3')) || 0;
    this.currencySymbol = this.getAttribute('data-currency-symbol') || '$';
    this.currencyCode = this.getAttribute('data-currency-code') || 'USD';
    this.variant1 = parseInt(this.getAttribute('data-variant-1')) || null;
    this.variant2 = parseInt(this.getAttribute('data-variant-2')) || null;
    this.variant3 = parseInt(this.getAttribute('data-variant-3')) || null;
    this.latestCart = null;
    this._addedGiftVariantIds = new Set();
    this._isAddingGift = false;
    this._isRemovingGift = false;
    this._pendingCartUpdate = null;
    this._debounceTimeout = null;
    
    this.init();
  }

  init() {
    this.updateProgress();
    this.setupEventListeners();
    // Evaluate immediately on load in case thresholds are already met
    // Fetch latest cart to ensure we have items for duplicate checks
    this.fetchUpdatedCartData();
  }

  setupEventListeners() {
    // Debounce cart updates to prevent multiple rapid calls
    const debouncedFetch = () => {
      if (this._debounceTimeout) {
        clearTimeout(this._debounceTimeout);
      }
      this._debounceTimeout = setTimeout(() => {
        this.fetchUpdatedCartData();
      }, 300); // 300ms debounce
    };

    document.addEventListener('cart:updated', debouncedFetch);
    document.addEventListener('cart:drawer:updated', debouncedFetch);
    // Some parts of the theme dispatch 'cart-updated' (hyphen)
    document.addEventListener('cart-updated', debouncedFetch);
  }

  // Calculate cart total excluding free gift items (for threshold checking)
  calculateCartTotalExcludingFreeGifts(cart) {
    if (!cart || !cart.items) return 0;
    let total = 0;
    for (const item of cart.items) {
      // Only count items that are NOT free gifts
      const isFreeGift = item.properties && item.properties._free_gift === 'true';
      if (!isFreeGift) {
        total += item.final_line_price || item.line_price || 0;
      }
    }
    return total;
  }

  async fetchUpdatedCartData(skipGiftChecks = false) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      // Calculate total excluding free gifts for threshold checking
      this.cartTotal = this.calculateCartTotalExcludingFreeGifts(cart);
      this.latestCart = cart;
      this.updateProgress();
      
      // Skip gift checks if we're in the middle of adding/removing (prevents infinite loops)
      if (!skipGiftChecks && !this._isAddingGift && !this._isRemovingGift) {
        // Check both adding and removing free gifts
        await this.maybeRemoveFreeGifts();
        await this.maybeAutoAddGifts();
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }

  updateProgress() {
    this.updateProgressText();
    this.updateProductStatuses();
    this.updateProgressBar();
  }

  updateProgressText() {
    const progressText = this.querySelector('[data-js-progress-text]');
    if (!progressText) return;

    // Convert USD thresholds to current currency
    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const convertedThreshold1 = Math.round(this.threshold1 * rate);
    const convertedThreshold2 = Math.round(this.threshold2 * rate);
    const convertedThreshold3 = Math.round(this.threshold3 * rate);

    // Only consider positive thresholds
    const thresholds = [convertedThreshold1, convertedThreshold2, convertedThreshold3].filter(t => t > 0);
    const sortedThresholds = [...thresholds].sort((a, b) => a - b);

    let nextThreshold = null;
    for (let i = 0; i < sortedThresholds.length; i++) {
      if (this.cartTotal < sortedThresholds[i]) {
        nextThreshold = sortedThresholds[i];
        break;
      }
    }

    if (sortedThresholds.length === 0) {
      // No thresholds configured; show nothing specific
      progressText.innerHTML = '';
      return;
    }

    if (nextThreshold === null) {
      progressText.innerHTML = '🎉 All free products unlocked!';
    } else {
      const remaining = nextThreshold - this.cartTotal;
      const remainingFormatted = this.formatMoney(remaining);

      let productName = 'Product';
      if (nextThreshold === convertedThreshold1) {
        productName = this.getProductName(1);
      } else if (nextThreshold === convertedThreshold2) {
        productName = this.getProductName(2);
      } else if (nextThreshold === convertedThreshold3) {
        productName = this.getProductName(3);
      }

      progressText.innerHTML = `You are ${remainingFormatted} away from <span>FREE</span> ${productName}`;
    }
  }

  updateProductStatuses() {
    // Convert USD thresholds to current currency
    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const convertedThreshold1 = Math.round(this.threshold1 * rate);
    const convertedThreshold2 = Math.round(this.threshold2 * rate);
    const convertedThreshold3 = Math.round(this.threshold3 * rate);

    // Only set status for thresholds that are configured (> 0)
    if (convertedThreshold1 > 0) this.setProductStatus(1, this.cartTotal >= convertedThreshold1); 
    if (convertedThreshold2 > 0) this.setProductStatus(2, this.cartTotal >= convertedThreshold2);
    if (convertedThreshold3 > 0) this.setProductStatus(3, this.cartTotal >= convertedThreshold3);
  }

  setProductStatus(productNumber, isUnlocked) {
    const productElement = this.querySelector(`[data-product="${productNumber}"]`);
    const statusElement = this.querySelector(`[data-js-product-status-${productNumber}]`);
    if (!productElement || !statusElement) return;

    if (isUnlocked) {
      productElement.classList.add('unlocked');
      productElement.classList.remove('locked');
      statusElement.innerHTML = '<span class="free-products-progress__check-icon">✅</span>';
    } else {
      productElement.classList.add('locked');
      productElement.classList.remove('unlocked');
    }
  }

  updateProgressBar() {
    const progressFill = this.querySelector('[data-js-progress-fill]');
    if (!progressFill) return;

    // Convert USD thresholds to current currency (same as shipping notice)
    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const convertedThreshold1 = Math.round(this.threshold1 * rate);
    const convertedThreshold2 = Math.round(this.threshold2 * rate);
    const convertedThreshold3 = Math.round(this.threshold3 * rate);

    // Sort thresholds in ascending order
    const thresholds = [convertedThreshold1, convertedThreshold2, convertedThreshold3].filter(t => t > 0);
    const sortedThresholds = [...thresholds].sort((a, b) => a - b);
    
    // Get the bar container to calculate available width
    const barContainer = this.querySelector('[data-js-progress-bar]');
    const availableWidth = barContainer ? barContainer.offsetWidth : 0;
    
    // Start the bar only after first product is unlocked
    if (this.cartTotal < sortedThresholds[0]) {
      // Before first threshold, show 0%
      progressFill.style.width = '0px';
      return;
    }
    
    // Check if we've reached the second product
    const hasReachedSecond = this.cartTotal >= sortedThresholds[1];
    
    // Check which segment we're in
    if (sortedThresholds.length === 1 || this.cartTotal >= sortedThresholds[sortedThresholds.length - 1]) {
      // Only one threshold or reached all thresholds - show 100%
      progressFill.style.width = '100%';
      return;
    }
    
    // Find which segment we're currently in
    for (let i = 0; i < sortedThresholds.length - 1; i++) {
      if (this.cartTotal >= sortedThresholds[i] && this.cartTotal < sortedThresholds[i + 1]) {
        // We're in segment between thresholds[i] and thresholds[i+1]
        const segmentStart = sortedThresholds[i];
        const segmentEnd = sortedThresholds[i + 1];
        const segmentRange = segmentEnd - segmentStart;
        const progressInSegment = this.cartTotal - segmentStart;
        const segmentPercent = (progressInSegment / segmentRange) * 100;
        
        // Calculate overall percent based on which segment we're in
        const basePercent = (i / sortedThresholds.length) * 100;
        const segmentContribution = (1 / sortedThresholds.length) * 100;
        let overallPercent = basePercent + (segmentPercent * segmentContribution / 100);
        
        // Add 85px when second product is reached
        if (hasReachedSecond && availableWidth > 0) {
          const percentWidth = (overallPercent / 100) * availableWidth;
          const finalWidth = percentWidth + 85;
          progressFill.style.width = `${Math.min(finalWidth, availableWidth)}px`;
        } else {
          progressFill.style.width = `${Math.min(overallPercent, 100)}%`;
        }
        return;
      }
    }
    
    // Fallback - should not reach here
    progressFill.style.width = '0%';
  }

  getProductName(productNumber) {
    const productElement = this.querySelector(`[data-product="${productNumber}"] .free-products-progress__product-name`);
    if (!productElement) return `Product ${productNumber}`;
    
    // Get the text content and remove "FREE" label if present
    let productName = productElement.textContent.trim();
    // Remove "FREE " from the beginning (with or without the label span)
    productName = productName.replace(/^FREE\s+/, '');
    
    return productName;
  }

  getVariantId(productNumber) {
    if (productNumber === 1) return this.variant1;
    if (productNumber === 2) return this.variant2;
    if (productNumber === 3) return this.variant3;
    return null;
  }

  isVariantAlreadyInCart(variantId) {
    if (!this.latestCart || !this.latestCart.items || !variantId) return false;
    // Convert to numbers for comparison to handle string/number mismatches
    const variantIdNum = parseInt(variantId);
    return this.latestCart.items.some(item => {
      const itemVariantId = parseInt(item.variant_id);
      return itemVariantId === variantIdNum;
    });
  }

  // Count how many times a variant appears in cart (for free gifts)
  getVariantQuantityInCart(variantId) {
    if (!this.latestCart || !this.latestCart.items || !variantId) return 0;
    const variantIdNum = parseInt(variantId);
    return this.latestCart.items.filter(item => {
      const itemVariantId = parseInt(item.variant_id);
      return itemVariantId === variantIdNum;
    }).reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  // Get free gift items from cart (items with _free_gift property)
  getFreeGiftItemsInCart() {
    if (!this.latestCart || !this.latestCart.items) return [];
    return this.latestCart.items.filter(item => 
      item.properties && 
      item.properties._free_gift === 'true'
    );
  }

  // Find which line number a variant is at in the cart
  findLineNumberForVariant(variantId) {
    if (!this.latestCart || !this.latestCart.items) return null;
    for (let i = 0; i < this.latestCart.items.length; i++) {
      const item = this.latestCart.items[i];
      if (item.variant_id === variantId || item.id === variantId) {
        return i + 1; // Shopify uses 1-based line numbers
      }
    }
    return null;
  }

  async maybeRemoveFreeGifts() {
    if (this._isRemovingGift || this._isAddingGift) return;

    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const t1 = Math.round(this.threshold1 * rate);
    const t2 = Math.round(this.threshold2 * rate);
    const t3 = Math.round(this.threshold3 * rate);

    // Check which thresholds are NOT met
    const checks = [
      { num: 1, threshold: t1, unlocked: t1 > 0 && this.cartTotal >= t1, variantId: this.getVariantId(1) },
      { num: 2, threshold: t2, unlocked: t2 > 0 && this.cartTotal >= t2, variantId: this.getVariantId(2) },
      { num: 3, threshold: t3, unlocked: t3 > 0 && this.cartTotal >= t3, variantId: this.getVariantId(3) }
    ];

    // Get all free gift items currently in cart
    const freeGiftItems = this.getFreeGiftItemsInCart();

    // For each free gift in cart, check if its threshold is still met
    for (const item of freeGiftItems) {
      const variantId = item.variant_id || item.id;
      
      // Find which threshold this variant belongs to
      const thresholdCheck = checks.find(c => c.variantId === variantId);
      
      // If we found the threshold and it's NOT unlocked, remove this item
      if (thresholdCheck && !thresholdCheck.unlocked) {
        await this.removeFreeGiftVariant(item);
        // Remove from tracking set
        this._addedGiftVariantIds.delete(variantId);
      }
      // Also remove if variant ID doesn't match any configured gift (shouldn't happen, but safety check)
      else if (!thresholdCheck) {
        // This free gift doesn't match any configured variant, but we'll leave it alone
        // as it might be from a different system or manually added
      }
    }
  }

  async removeFreeGiftVariant(cartItem) {
    try {
      this._isRemovingGift = true;
      
      // Find line number for this item
      const lineNumber = this.findLineNumberForVariant(cartItem.variant_id || cartItem.id);
      
      if (!lineNumber) {
        console.warn('Could not find line number for free gift variant:', cartItem.variant_id);
        return;
      }

      // Remove item by setting quantity to 0
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: lineNumber, quantity: 0 })
      });

      console.log('Removed free gift from cart:', cartItem.title);

      // Refresh cart UI if helper is present; otherwise, emit events
      if (window.refreshCart && typeof window.refreshCart === 'function') {
        window.refreshCart();
      } else {
        document.dispatchEvent(new Event('cart:updated'));
        document.dispatchEvent(new Event('cart-updated'));
      }
      
      // Fetch updated cart to keep internal state in sync (skip gift checks to prevent loops)
      // The cart update events will trigger another fetchUpdatedCartData which will check gifts
      await this.fetchUpdatedCartData(true);
    } catch (e) {
      console.error('Failed to remove free gift variant', e);
    } finally {
      this._isRemovingGift = false;
    }
  }

  async maybeAutoAddGifts() {
    if (this._isAddingGift || this._isRemovingGift) return;

    const rate = window.Shopify && window.Shopify.currency ? window.Shopify.currency.rate : 1;
    const t1 = Math.round(this.threshold1 * rate);
    const t2 = Math.round(this.threshold2 * rate);
    const t3 = Math.round(this.threshold3 * rate);

    const checks = [
      { num: 1, unlocked: t1 > 0 && this.cartTotal >= t1, variantId: this.getVariantId(1) },
      { num: 2, unlocked: t2 > 0 && this.cartTotal >= t2, variantId: this.getVariantId(2) },
      { num: 3, unlocked: t3 > 0 && this.cartTotal >= t3, variantId: this.getVariantId(3) }
    ];

    // Sync tracking set with actual cart state FIRST
    // Reset and rebuild from actual cart
    this._addedGiftVariantIds.clear();
    for (const c of checks) {
      if (!c.variantId) continue;
      // If variant is in cart (especially as free gift), track it
      if (this.isVariantAlreadyInCart(c.variantId)) {
        this._addedGiftVariantIds.add(c.variantId);
      }
    }

    // Now check what needs to be added (only variants not in cart yet)
    for (const c of checks) {
      if (!c.variantId || !c.unlocked) continue;
      
      const variantIdStr = String(c.variantId);
      
      // GLOBAL CHECK: Is any instance currently adding this?
      if (window._freeGiftsGlobalLock.adding.has(variantIdStr)) {
        console.log('Variant being added by another instance, skipping:', c.variantId);
        continue;
      }
      
      // Check actual cart quantity - if already in cart, skip
      const quantity = this.getVariantQuantityInCart(c.variantId);
      if (quantity > 0) {
        this._addedGiftVariantIds.add(c.variantId);
        continue;
      }
      
      // Double check tracking set
      if (this._addedGiftVariantIds.has(c.variantId)) {
        continue;
      }
      
      // Only add if NOT in cart at all
      if (!this.isVariantAlreadyInCart(c.variantId)) {
        // Mark as adding BEFORE the async call to prevent duplicates
        this._addedGiftVariantIds.add(c.variantId);
        await this.addFreeGiftVariant(c.variantId);
      }
    }
  }

  async addFreeGiftVariant(variantId) {
    const variantIdStr = String(variantId);
    
    // GLOBAL LOCK: Check if any instance is already adding this variant
    if (window._freeGiftsGlobalLock.adding.has(variantIdStr)) {
      console.log('Another instance is already adding variant:', variantId);
      return;
    }
    
    // Check if pending with recent timestamp
    const pendingTimestamp = window._freeGiftsGlobalLock.pending.get(variantIdStr);
    if (pendingTimestamp && (Date.now() - pendingTimestamp) < 3000) {
      console.log('Variant add still pending from recent operation:', variantId);
      return;
    }
    
    try {
      this._isAddingGift = true;
      
      // Mark globally as adding
      window._freeGiftsGlobalLock.adding.add(variantIdStr);
      window._freeGiftsGlobalLock.pending.set(variantIdStr, Date.now());
      
      // Final check - if already in cart, abort
      const quantity = this.getVariantQuantityInCart(variantId);
      if (quantity > 0) {
        console.log('Free gift variant already in cart (qty:', quantity, '), skipping:', variantId);
        // Clean up locks
        window._freeGiftsGlobalLock.adding.delete(variantIdStr);
        window._freeGiftsGlobalLock.pending.delete(variantIdStr);
        return;
      }
      
      console.log('Adding free gift variant:', variantId);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { _free_gift: 'true' } })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      const result = await response.json();
      console.log('Successfully added free gift variant:', variantId, result);
      
      // Wait a moment for cart to update on server
      await new Promise(resolve => setTimeout(resolve, 200));

      // Refresh cart UI WITHOUT dispatching events (to prevent loops)
      if (window.refreshCart && typeof window.refreshCart === 'function') {
        window.refreshCart();
      }
      
      // Fetch updated cart but skip gift checks (events will trigger another check)
      await this.fetchUpdatedCartData(true);
      
      // Keep lock for a bit longer to prevent rapid re-adds
      setTimeout(() => {
        window._freeGiftsGlobalLock.adding.delete(variantIdStr);
        window._freeGiftsGlobalLock.pending.delete(variantIdStr);
      }, 1000);
    } catch (e) {
      console.error('Failed to add free gift variant', e);
      // Remove from tracking if add failed so it can be retried
      this._addedGiftVariantIds.delete(variantId);
      // Clean up locks on error
      window._freeGiftsGlobalLock.adding.delete(variantIdStr);
      window._freeGiftsGlobalLock.pending.delete(variantIdStr);
    } finally {
      this._isAddingGift = false;
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return `${this.currencySymbol}${amount.toFixed(2)}`;
  }
}

// Global lock to prevent duplicate adds across all instances
window._freeGiftsGlobalLock = window._freeGiftsGlobalLock || {
  adding: new Set(),
  pending: new Map(), // variantId -> timestamp
  timeout: 5000 // 5 second timeout for pending operations
};

// Clean up stale pending entries
setInterval(() => {
  const now = Date.now();
  for (const [variantId, timestamp] of window._freeGiftsGlobalLock.pending.entries()) {
    if (now - timestamp > window._freeGiftsGlobalLock.timeout) {
      window._freeGiftsGlobalLock.pending.delete(variantId);
      window._freeGiftsGlobalLock.adding.delete(variantId);
    }
  }
}, 1000);

// Register the custom element
customElements.define('free-products-progress', FreeProductsProgress);

// Also register as a regular class for manual initialization
window.FreeProductsProgress = FreeProductsProgress;

// Fallback: manually initialize if custom element doesn't work
document.addEventListener('DOMContentLoaded', function() {
  const progressElements = document.querySelectorAll('free-products-progress');
  if (progressElements.length === 0) {
    console.log('No free-products-progress elements found!');
  }
});